import { apiRequest, setContext } from "./api";

export const memberApi = {
  platformIdentity: () =>
    apiRequest("/api/platform-identity", { contextOptional: true }),
  discoverLeagues: (location) =>
    apiRequest(`/api/geo-discovery?location=${encodeURIComponent(location)}`, {
      contextOptional: true,
    }),
  previewTeamJoin: (publicId) =>
    apiRequest(`/api/team-join-links/${publicId}`, { contextOptional: true }),
  requestTeamJoin: (publicId) =>
    apiRequest(`/api/team-join-links/${publicId}/requests`, { method: "POST" }),
  teamRegistration: (publicId) =>
    apiRequest(`/api/team-registration-links/${publicId}`, {
      contextOptional: true,
    }),
  captainTeamsForRegistration: (publicId) =>
    apiRequest(`/api/team-registration-links/${publicId}/my-teams`, {
      contextOptional: true,
    }),
  captainProfileForRegistration: (publicId) =>
    apiRequest(`/api/team-registration-links/${publicId}/captain-profile`, {
      contextOptional: true,
    }),
  registerCaptainTeam: (publicId, value) =>
    apiRequest(`/api/team-registration-links/${publicId}/register`, {
      method: "POST",
      contextOptional: true,
      body: JSON.stringify(value),
    }),
  tournament: (publicId) =>
    apiRequest(`/api/tournament-registration-links/${publicId}`, {
      contextOptional: true,
    }),
  registerForTournament: (publicId) =>
    apiRequest(`/api/tournament-registration-links/${publicId}/register`, {
      method: "POST",
      contextOptional: true,
    }),
  withdrawFromTournament: (publicId) =>
    apiRequest(`/api/tournament-registration-links/${publicId}/withdraw`, {
      method: "POST",
      contextOptional: true,
    }),
  tournamentRegistrations: () =>
    apiRequest("/api/tournament-registration-links/my-registrations", {
      contextOptional: true,
    }),
  organizations: () =>
    apiRequest("/api/account/organizations", { contextOptional: true }),
  account: () => apiRequest("/api/account/me", { contextOptional: true }),
  previewClaim: (token) =>
    apiRequest(`/api/player-claims/preview/${encodeURIComponent(token)}`, {
      contextOptional: true,
    }),
  previewTeamInvitation: (token) =>
    apiRequest(`/api/team-roster-invitations/${encodeURIComponent(token)}`, {
      contextOptional: true,
    }),
  acceptTeamInvitation: (token) =>
    apiRequest(
      `/api/team-roster-invitations/${encodeURIComponent(token)}/accept`,
      { method: "POST", contextOptional: true },
    ),
  rejectTeamInvitation: (token) =>
    apiRequest(
      `/api/team-roster-invitations/${encodeURIComponent(token)}/reject`,
      { method: "POST", contextOptional: true },
    ),
  async confirmClaim(token, profile) {
    const result = await apiRequest("/api/player-claims/confirm", {
      method: "POST",
      contextOptional: true,
      body: JSON.stringify({ token, ...profile }),
    });
    await setContext({ organizationId: result.organizationId });
    return result;
  },
  async selectOrganization(organization) {
    await setContext({
      organizationId: organization.organizationId,
      organizationName: organization.organizationName,
    });
    return apiRequest("/api/account/context", {
      method: "POST",
      contextOptional: true,
      body: JSON.stringify({ organizationId: organization.organizationId }),
    });
  },
  dashboard: () => apiRequest("/api/member/dashboard"),
  paymentStatus: () => apiRequest("/api/member/payments/status"),
  createFeeCheckout: (feeAssessmentId) =>
    apiRequest("/api/member/payments/checkout", {
      method: "POST",
      body: JSON.stringify({ feeAssessmentId }),
    }),
  captainTeams: () => apiRequest("/api/captain/teams"),
  captainTeam: (teamId) => apiRequest(`/api/captain/teams/${teamId}`),
  captainRoster: (registrationId) =>
    apiRequest(`/api/captain/teams/registrations/${registrationId}/roster`),
  captainJoinRequests: (registrationId) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/join-requests`,
    ),
  approveCaptainJoinRequest: (registrationId, requestId) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/join-requests/${requestId}/approve`,
      { method: "POST" },
    ),
  rejectCaptainJoinRequest: (registrationId, requestId) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/join-requests/${requestId}/reject`,
      { method: "POST" },
    ),
  setCaptainTeamAcceptingPlayers: (registrationId, isAcceptingPlayers) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/accepting-players`,
      { method: "PUT", body: JSON.stringify({ isAcceptingPlayers }) },
    ),
  availableMembers: (registrationId, search = "") =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/available-members?search=${encodeURIComponent(search)}`,
    ),
  memberByEmail: (registrationId, email) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/member-by-email?email=${encodeURIComponent(email)}`,
    ),
  addRosterMember: (
    registrationId,
    playerMembershipId,
    channel = null,
    forceAccept = false,
  ) =>
    apiRequest(`/api/captain/teams/registrations/${registrationId}/roster`, {
      method: "POST",
      body: JSON.stringify({ playerMembershipId, channel, forceAccept }),
    }),
  forceAcceptRosterMember: (registrationId, playerMembershipId) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/roster/${playerMembershipId}/force-accept`,
      { method: "POST" },
    ),
  updateRosterMemberEmail: (registrationId, playerMembershipId, email) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/roster/${playerMembershipId}/email`,
      { method: "PUT", body: JSON.stringify({ email }) },
    ),
  removeRosterMember: (registrationId, playerMembershipId) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/roster/${playerMembershipId}`,
      { method: "DELETE" },
    ),
  addNewRosterPlayer: (registrationId, value) =>
    apiRequest(
      `/api/captain/teams/registrations/${registrationId}/new-player`,
      { method: "POST", body: JSON.stringify(value) },
    ),
  inviteRosterPlayer: (playerMembershipId, channel, deliveryTarget) =>
    apiRequest(
      `/api/player-memberships/${playerMembershipId}/claim-invitations/captain`,
      { method: "POST", body: JSON.stringify({ channel, deliveryTarget }) },
    ),
  sendRosterPlayerInvitation: (playerMembershipId, invitation) =>
    apiRequest(
      `/api/player-memberships/${playerMembershipId}/claim-invitations/${invitation.id}/send`,
      {
        method: "POST",
        body: JSON.stringify({ token: invitation.token, channel: "Email" }),
      },
    ),
  delegations: (teamId) =>
    apiRequest(`/api/captain/teams/${teamId}/delegations`),
  grantDelegation: (teamId, value) =>
    apiRequest(`/api/captain/teams/${teamId}/delegations`, {
      method: "POST",
      body: JSON.stringify(value),
    }),
  revokeDelegation: (teamId, delegationId) =>
    apiRequest(`/api/captain/teams/${teamId}/delegations/${delegationId}`, {
      method: "DELETE",
    }),
  submitTeamRegistration: (registrationId) =>
    apiRequest(`/api/captain/teams/registrations/${registrationId}/submit`, {
      method: "POST",
    }),
  updateProfile: (profile) =>
    apiRequest("/api/member/profile", {
      method: "PUT",
      body: JSON.stringify(profile),
    }),
  notifications: (unreadOnly = false) =>
    apiRequest(`/api/member/notifications?unreadOnly=${unreadOnly}`),
  markNotificationRead: (id) =>
    apiRequest(`/api/member/notifications/${id}/read`, { method: "POST" }),
  markAllNotificationsRead: () =>
    apiRequest("/api/member/notifications/read-all", { method: "POST" }),
  removeNotification: (id) =>
    apiRequest(`/api/member/notifications/${id}`, { method: "DELETE" }),
  notificationCapabilities: () =>
    apiRequest("/api/member/notifications/capabilities"),
  notificationPreferences: () =>
    apiRequest("/api/member/notifications/preferences"),
  saveNotificationPreferences: (value) =>
    apiRequest("/api/member/notifications/preferences", {
      method: "PUT",
      body: JSON.stringify(value),
    }),
  savePushSubscription: (value) =>
    apiRequest("/api/member/notifications/push-subscriptions", {
      method: "PUT",
      body: JSON.stringify(value),
    }),
  removePushSubscription: (endpoint) =>
    apiRequest(
      `/api/member/notifications/push-subscriptions?endpoint=${encodeURIComponent(endpoint)}`,
      { method: "DELETE" },
    ),
  matchPackage: (matchId) =>
    apiRequest(`/api/member/matches/${matchId}/package`),
  matchScheduleChanges: (matchId) =>
    apiRequest(`/api/matches/${matchId}/schedule-changes`),
  proposeMatchReschedule: (matchId, value) =>
    apiRequest(`/api/matches/${matchId}/schedule-changes/proposals`, {
      method: "POST",
      body: JSON.stringify(value),
    }),
  approveMatchReschedule: (matchId, changeId) =>
    apiRequest(`/api/matches/${matchId}/schedule-changes/${changeId}/approve`, {
      method: "POST",
    }),
  declineMatchReschedule: (matchId, changeId, reason) =>
    apiRequest(`/api/matches/${matchId}/schedule-changes/${changeId}/decline`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  controllerWifi: (matchId) =>
    apiRequest(`/api/member/matches/${matchId}/controller-wifi`),
  scoreboardGames: (matchId) =>
    apiRequest(`/api/member/matches/${matchId}/scoreboard/games`),
  matchLiveStatus: (matchId) =>
    apiRequest(`/api/member/matches/${matchId}/scoreboard/live`),
  scoreboardCompletion: (matchId) =>
    apiRequest(`/api/member/matches/${matchId}/scoreboard/completion`),
  submitScoreboardCompletion: (matchId) =>
    apiRequest(`/api/member/matches/${matchId}/scoreboard/completion/submit`, {
      method: "POST",
    }),
  saveLineup: (matchId, registrationId, assignments, confirm = false) =>
    apiRequest(`/api/member/matches/${matchId}/lineups/${registrationId}`, {
      method: "PUT",
      body: JSON.stringify({ assignments, confirm }),
    }),
  startScoring: (matchId, deviceId) =>
    apiRequest(`/api/member/matches/${matchId}/scoring/start`, {
      method: "POST",
      body: JSON.stringify({ deviceId }),
    }),
  scoreEvents: (sessionId, value) =>
    apiRequest(`/api/member/scoring/${sessionId}/events`, {
      method: "POST",
      body: JSON.stringify(value),
    }),
  completeScoring: (sessionId, value) =>
    apiRequest(`/api/member/scoring/${sessionId}/complete`, {
      method: "POST",
      body: JSON.stringify(value),
    }),
  approveMatch: (matchId, registrationId) =>
    apiRequest(`/api/member/matches/${matchId}/approvals/${registrationId}`, {
      method: "POST",
    }),
  handoffScoring: (sessionId, value) =>
    apiRequest(`/api/member/scoring/${sessionId}/handoff`, {
      method: "POST",
      body: JSON.stringify(value),
    }),
  scoresheet: (matchId) =>
    apiRequest(`/api/member/matches/${matchId}/scoresheet`),
  startScoresheet: (matchId) =>
    apiRequest(`/api/member/matches/${matchId}/scoresheet/start`, {
      method: "POST",
    }),
  scoresheetEvents: (scoresheetId, value) =>
    apiRequest(`/api/member/scoresheets/${scoresheetId}/events`, {
      method: "POST",
      body: JSON.stringify(value),
    }),
  submitScoresheet: (scoresheetId, expectedVersion) =>
    apiRequest(`/api/member/scoresheets/${scoresheetId}/submit`, {
      method: "POST",
      body: JSON.stringify({ expectedVersion }),
    }),
  grantScoresheetAssistant: (matchId, awayCaptainAccountId, expiresAt = null) =>
    apiRequest(`/api/member/matches/${matchId}/scoresheet/assistant-grants`, {
      method: "POST",
      body: JSON.stringify({ awayCaptainAccountId, expiresAt }),
    }),
  revokeScoresheetAssistant: (matchId, grantId) =>
    apiRequest(
      `/api/member/matches/${matchId}/scoresheet/assistant-grants/${grantId}`,
      { method: "DELETE" },
    ),
  uploadEvidence: (matchId, file, notes = "") => {
    const body = new FormData();
    body.append("file", file);
    body.append("evidenceType", "ScorecardPhoto");
    body.append("notes", notes);
    return apiRequest(`/api/member/matches/${matchId}/evidence`, {
      method: "POST",
      body,
    });
  },
};
